import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Modal,
  TextField,
} from "@mui/material";
import { InputOptionComponent } from "components";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useCreateAccount,
  useEditExternalUser,
  useGetExternalUserDetail,
} from "../service/ClientManagementService";
import { formatNumberOnly, formatPhoneNumberPayloadBE } from "services/util";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import { useParams } from "react-router-dom";

const roleOptions = [
  { label: "Admin", value: "COMPANY ADMIN" },
  { label: "User", value: "COMPANY USER" },
];

const statusOptions = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const CreateEditAccountModal = ({ modalProps = {}, id, onClose }) => {
  const { id: companyId } = useParams();
  const { setMessage } = useSnackbar();
  const [formData, setFormData] = useState({});

  const { response: accountDetail, loading: loadingDetail } =
    useGetExternalUserDetail({ id });
  const { mutate: createAccount, loading: loadingCreate } = useCreateAccount();
  const { mutate: updateAccount, loading: loadingUpdate } = useEditExternalUser(
    {
      id,
    },
  );

  const isEdit = useMemo(() => Boolean(id), [id]);

  const handleSubmitForm = useCallback(async () => {
    const modifiedFormData = {
      ...formData,
      phone_number: formatPhoneNumberPayloadBE(formData?.phone_number),
    };
    if (!formData?.full_name) {
      setMessage({
        desc: "Name cannot be blank",
        type: "error",
      });
      return;
    }

    if (!formData?.email) {
      setMessage({
        desc: "Email cannot be blank",
        type: "error",
      });
      return;
    }

    if (!formData?.phone_number) {
      setMessage({
        desc: "Phone number cannot be blank",
        type: "error",
      });
      return;
    }

    if (!id) {
      const response = await createAccount({
        ...modifiedFormData,
        company_id: +companyId,
      });

      if (
        response?.info?.description === "User is created successfully" ||
        response?.info?.title === "success"
      ) {
        setMessage({
          desc: "Successfully created new account.",
          type: "success",
        });
        onClose();
      } else {
        const errMessage =
          response ?? "Failed to create account. Please try again.";
        setMessage({
          desc: errMessage,
          type: "error",
        });
      }
    } else {
      const detailAccount = accountDetail?.data ?? {};
      // send only modified field
      const updateAccountPayload = Object.keys(modifiedFormData)?.reduce(
        (prev, currKey) => {
          const isKeyModified =
            detailAccount?.[currKey] !== modifiedFormData?.[currKey];
          return {
            ...prev,
            ...(isKeyModified
              ? { [currKey]: modifiedFormData?.[currKey] }
              : {}),
          };
        },
        {},
      );

      const response = await updateAccount(updateAccountPayload);

      if (
        response?.info?.description === "User is updated successfully" ||
        response?.info?.title === "success"
      ) {
        setMessage({
          desc: "Successfully updated account.",
          type: "success",
        });
        onClose();
      } else {
        const errMessage =
          response ?? "Failed to edit account. Please try again.";
        setMessage({
          desc: errMessage,
          type: "error",
        });
      }
    }
  }, [
    accountDetail,
    formData,
    id,
    createAccount,
    companyId,
    setMessage,
    onClose,
    updateAccount,
  ]);

  const handleOnChangeFormInput = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // auto fill form when edit modal is opened
  useEffect(() => {
    if (id && accountDetail) {
      const detail = accountDetail?.data || {};
      setFormData({
        full_name: detail?.full_name,
        email: detail?.email,
        phone_number: detail?.phone_number?.split("+62")?.[1],
        status: detail?.status,
        role: detail?.role,
      });
    }
  }, [id, loadingDetail, accountDetail]);

  return (
    <Modal {...modalProps}>
      <Box
        sx={{
          position: "absolute",
          top: "55%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          overflow: "scroll",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          px: "20px",
          py: "25px",
        }}
      >
        <div className='create-edit-modal-content'>
          <div className='font-semibold mb-12'>
            {`${isEdit ? "Edit" : "Create New"} Account`}
          </div>

          <div className='mb-8'>
            <div className='grid gap-4 mb-5'>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <div className='form-input'>
                    <p className='form-input-label'>Name</p>
                    <TextField
                      placeholder='Insert account name'
                      value={formData?.full_name}
                      onChange={({ target: { value } } = {}) =>
                        handleOnChangeFormInput("full_name", value)
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className='form-input'>
                    <p className='form-input-label'>E-mail address</p>
                    <TextField
                      placeholder='Insert e-mail address'
                      value={formData?.email}
                      onChange={({ target: { value } } = {}) =>
                        handleOnChangeFormInput("email", value)
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className='form-input'>
                    <p className='form-input-label'>Phone Number</p>
                    <TextField
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            sx={{
                              backgroundColor: "#EFF1F2",
                              padding: "20px 14px",
                              borderRight: "1px solid #727273",
                              borderTopLeftRadius: "10px",
                              borderBottomLeftRadius: "10px",
                            }}
                            position='start'
                          >
                            <p>+62</p>
                          </InputAdornment>
                        ),
                      }}
                      placeholder='Insert phone number'
                      value={formData?.phone_number}
                      onChange={({ target: { value } } = {}) =>
                        handleOnChangeFormInput(
                          "phone_number",
                          formatNumberOnly(value),
                        )
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className='form-input'>
                    <p className='form-input-label'>Role</p>
                    <InputOptionComponent
                      width='full'
                      label='Select Role'
                      options={roleOptions}
                      value={roleOptions?.find(
                        (el) => el?.value === formData?.role,
                      )}
                      onSelect={(opt) =>
                        handleOnChangeFormInput("role", opt?.value)
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className='form-input'>
                    <p className='form-input-label'>Status</p>
                    <InputOptionComponent
                      width='full'
                      label='Select status'
                      options={statusOptions}
                      value={statusOptions?.find(
                        (el) =>
                          el?.value === (isEdit ? formData?.status : "ACTIVE"),
                      )}
                      disabled={!isEdit}
                      onSelect={(opt) =>
                        handleOnChangeFormInput("status", opt?.value)
                      }
                    />
                  </div>
                </Grid>
              </Grid>
            </div>
          </div>

          <div className='flex flex-row gap-4 justify-end'>
            {isEdit && (
              <Button
                className='w-[76px] py-2 px-[17px]'
                onClick={onClose}
                variant='outlined'
                disabled={loadingCreate || loadingUpdate}
              >
                <p className='font-bold text-xs'>Cancel</p>
              </Button>
            )}
            <Button
              className='!bg-[#556FB5] w-[200px] py-2 px-[17px]'
              onClick={handleSubmitForm}
              variant='contained'
              disabled={loadingCreate || loadingUpdate}
            >
              <p className='text-xs font-bold'>{`${
                isEdit ? "Save Changes" : "Create Account"
              }`}</p>
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default CreateEditAccountModal;
